import { useState, useEffect, useCallback } from "react";
import { Badge, Card, MetricCard, btnPrimary, btnSecondary, inputStyle, labelStyle, statusColor } from "./shared";

const normalizeAddr = (addr) => (addr || "")
  .replace(/[\u00a0\u00ad\u200b\u200c\u200d\u2009\u202f\u2028\u2029\u3000\t\r\n]+/g, " ")
  .toLowerCase()
  .replace(/\bavenue\b/g, "ave").replace(/\bstreet\b/g, "st")
  .replace(/\bdrive\b/g, "dr").replace(/\bboulevard\b/g, "blvd")
  .replace(/\blane\b/g, "ln").replace(/\bcourt\b/g, "ct")
  .replace(/\broad\b/g, "rd").replace(/\bplace\b/g, "pl")
  .replace(/\bwest\b/g, "west")
  .replace(/\bw\b/g, "west").replace(/\be\b/g, "east")
  .replace(/\bn\b/g, "north").replace(/\bs\b/g, "south")
  .replace(/#\s*/g, " ")
  .replace(/\s+(unit|apt|apartment|suite|ste|no\.?|num|floor)\s*[\w-]*\s*$/i, "")
  .replace(/[.,]/g, "").replace(/\s+/g, " ").trim();

const getPropertyTenants = (prop, tenants) => {
  const pa = normalizeAddr(prop.address);
  const paNum = (pa.match(/^\d+/) || [])[0];
  const paWords = pa.split(" ").filter(w => w.length > 3 && !/^\d+$/.test(w));
  return tenants.filter(t => {
    const ta = normalizeAddr(t.address);
    if (!ta || !pa) return false;
    if (ta === pa) return true;
    if (ta.startsWith(pa + ",") || ta.startsWith(pa + " ")) return true;
    if (pa.startsWith(ta + ",") || pa.startsWith(ta + " ")) return true;
    if (ta.includes(pa)) return true;
    if (paNum && (ta.match(/^\d+/) || [])[0] === paNum && paWords.every(w => ta.includes(w))) return true;
    return false;
  });
};

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const NOTE_CATEGORIES = ["General", "Vendor", "Incident", "Inspection"];
const NOTE_CAT_COLOR = { Vendor: "#3b82f6", Incident: "#dc2626", Inspection: "#B08D57", General: "#163C2F" };

function PropertyNotes({ property, supabase, session }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [category, setCategory] = useState("General");
  const [saving, setSaving] = useState(false);

  const loadNotes = useCallback(async () => {
    const { data } = await supabase
      .from("property_notes")
      .select("*")
      .eq("property_id", property.id)
      .order("created_at", { ascending: false });
    setNotes(data || []);
    setLoading(false);
  }, [supabase, property.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    const enteredBy = session?.user?.user_metadata?.full_name
      || session?.user?.user_metadata?.name
      || session?.user?.email
      || "Unknown";
    const { error } = await supabase.from("property_notes").insert([{
      property_id: property.id,
      user_id: session?.user?.id,
      note: noteText.trim(),
      category,
      entered_by: enteredBy,
    }]);
    if (error) { console.error("Failed to add note:", error.message); }
    setNoteText("");
    setCategory("General");
    await loadNotes();
    setSaving(false);
  };

  const deleteNote = async (id) => {
    await supabase.from("property_notes").delete().eq("id", id);
    setNotes(prev => prev.filter(n => n.id !== id));
  };

  const fmtNoteDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <Card>
      <h2 style={{
        fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20,
        color: "#163C2F", margin: "0 0 18px",
      }}>Property Notes</h2>

      {/* Add note form */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Note</label>
          <textarea
            placeholder="Type a note about this property…"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 72 }}
          />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "0 0 170px" }}>
            <label style={labelStyle}>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
              {NOTE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button
            onClick={addNote}
            disabled={saving || !noteText.trim()}
            style={{
              ...btnPrimary, padding: "10px 22px",
              opacity: saving || !noteText.trim() ? 0.5 : 1,
            }}
          >
            {saving ? "Adding…" : "Add Note"}
          </button>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: "#E8E4DC", marginBottom: 20 }} />

      {/* Notes list */}
      {loading ? (
        <div style={{ fontSize: 13, color: "#9B9690", padding: "20px 0", textAlign: "center" }}>Loading notes…</div>
      ) : notes.length === 0 ? (
        <div style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic", padding: "16px 0", textAlign: "center" }}>
          No notes yet. Add one above.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {notes.map(n => (
            <div key={n.id} style={{
              padding: "16px 18px", background: "#F5F1EA", borderRadius: 10,
              border: "1px solid #E8E4DC",
              display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 8 }}>
                  <Badge label={n.category || "General"} color={NOTE_CAT_COLOR[n.category] || "#163C2F"} />
                </div>
                <div style={{ fontSize: 14, color: "#2B2B2B", lineHeight: 1.65, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {n.note}
                </div>
                <div style={{ fontSize: 12, color: "#9B9690", marginTop: 8 }}>
                  Added by {n.entered_by || "Unknown"} &middot; {fmtNoteDate(n.created_at)}
                </div>
              </div>
              <button
                onClick={() => deleteNote(n.id)}
                title="Delete note"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  padding: 6, flexShrink: 0, color: "#9B9690", lineHeight: 1,
                  borderRadius: 6, transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "#dc2626"; e.currentTarget.style.background = "#dc262612"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#9B9690"; e.currentTarget.style.background = "transparent"; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" /><path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function PropertyDetail({ property, tenants, payments, maintenance, expenses, setPage, setSelectedProperty, supabase, session }) {
  const propTenants = getPropertyTenants(property, tenants);
  const tenantNames = new Set(propTenants.map(t => t.name));
  const currentYear = new Date().getFullYear();
  const today = new Date();

  const projectedAnnual = propTenants
    .filter(t => t.tenant_type !== "non_paying" && !t.is_complimentary)
    .reduce((sum, t) => sum + (Number(t.rent) || 0), 0) * 12;

  const propPayments = payments.filter(p => tenantNames.has(p.tenant));

  const collectedYTD = propPayments
    .filter(p => p.status === "paid" && p.paid && p.paid.startsWith(String(currentYear)))
    .reduce((sum, p) => sum + (Number(p.amount) || 0) + (Number(p.late_fee) || 0), 0);

  const occupied = propTenants.length;
  const totalUnits = property.units || 1;
  const occupancyPct = totalUnits > 0 ? Math.round((occupied / totalUnits) * 100) : 0;

  const normPropAddr = normalizeAddr(property.address);
  const propMaint = maintenance.filter(m => {
    const nm = normalizeAddr(m.property || "");
    return nm === normPropAddr || nm.includes(normPropAddr) || normPropAddr.includes(nm);
  });
  const openMaint = propMaint.filter(m => m.status !== "resolved");

  const sixtyDaysLater = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
  const expiringTenants = propTenants.filter(t => {
    if (!t.lease_end) return false;
    const end = new Date(t.lease_end + "T00:00:00");
    return end >= today && end <= sixtyDaysLater;
  });

  const yearPayments = propPayments
    .filter(p => p.due && p.due.startsWith(String(currentYear)))
    .sort((a, b) => new Date(b.due) - new Date(a.due));

  const propExpenses = expenses.filter(e => e.property_id === property.id);
  const ytdExpenses = propExpenses.filter(e => e.date && e.date.startsWith(String(currentYear)));
  const totalExpenses = ytdExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const netProfit = collectedYTD - totalExpenses;

  const daysLeft = (dateStr) => {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr + "T00:00:00") - today) / (1000 * 60 * 60 * 24));
  };

  const sectionHead = {
    fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 20,
    color: "#163C2F", margin: "0 0 14px",
  };
  const thStyle = {
    fontSize: 11, color: "#9B9690", fontFamily: "'Manrope', sans-serif",
    textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600,
    padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #E8E4DC", whiteSpace: "nowrap",
  };
  const tdStyle = {
    fontSize: 13, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif",
    padding: "10px 12px", borderBottom: "1px solid #F5F1EA",
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => { setSelectedProperty(null); setPage("Properties"); }}
          style={{ ...btnSecondary, fontSize: 12, padding: "5px 14px", marginBottom: 14, display: "inline-flex", alignItems: "center", gap: 5 }}
        >
          &larr; Back to Properties
        </button>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 34, color: "#2B2B2B", margin: 0 }}>
          {property.address}
        </h1>
        <div style={{ color: "#9B9690", fontSize: 14, fontFamily: "'Manrope', sans-serif", marginTop: 4 }}>
          {totalUnits} unit{totalUnits !== 1 ? "s" : ""} &middot; {occupied} occupied &middot; {Math.max(0, totalUnits - occupied)} vacant
        </div>
      </div>

      {/* Lease expiration alert */}
      {expiringTenants.length > 0 && (
        <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B44", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>&#9888;&#65039;</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#92400E", marginBottom: 3 }}>Lease Expiring Soon</div>
            <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
              {expiringTenants.map((t, i) => {
                const d = daysLeft(t.lease_end);
                return (
                  <span key={i}>
                    {i > 0 && " · "}
                    <strong>{t.name}</strong>{" "}&mdash;{" "}
                    {d != null && d <= 0 ? "expired" : `${d} day${d === 1 ? "" : "s"} remaining`}
                    {" "}({fmtDate(t.lease_end)})
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, marginBottom: 28 }}>
        <MetricCard label="Projected Annual Revenue" value={`$${fmt(projectedAnnual)}`} sub="Based on current rents" />
        <MetricCard label={`Collected ${currentYear}`} value={`$${fmt(collectedYTD)}`} sub="Payments received YTD" />
        <MetricCard label="Occupancy" value={`${occupancyPct}%`} sub={`${occupied} of ${totalUnits} unit${totalUnits !== 1 ? "s" : ""}`} />
        <MetricCard label="Open Tickets" value={String(openMaint.length)} sub="Maintenance issues" />
      </div>

      {/* Tenants + Maintenance row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 20, marginBottom: 20 }}>

        {/* Current Tenants */}
        <Card>
          <h2 style={sectionHead}>Current Tenants</h2>
          {propTenants.length === 0 && Math.max(0, totalUnits - occupied) === 0 ? (
            <div style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic" }}>No tenants assigned to this property.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {propTenants.map((t, i) => {
                const days = daysLeft(t.lease_end);
                return (
                  <div key={i} style={{ padding: "12px 14px", background: "#F5F1EA", borderRadius: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#2B2B2B" }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: "#9B9690", marginTop: 2 }}>
                          {t.property || "Main Unit"}
                          {t.tenant_type !== "non_paying" && t.rent ? ` · $${Number(t.rent).toLocaleString()}/mo` : ""}
                        </div>
                      </div>
                      <Badge label={t.status || "active"} color={statusColor[t.status] || "#163C2F"} />
                    </div>
                    <div style={{ fontSize: 12, color: "#9B9690", marginTop: 8 }}>
                      {fmtDate(t.lease_start)} &rarr; {t.lease_end ? fmtDate(t.lease_end) : "Month-to-Month"}
                    </div>
                    {days !== null && days <= 90 && (
                      <div style={{ marginTop: 5, fontSize: 11, fontWeight: 600, color: days <= 30 ? "#dc2626" : "#B08D57" }}>
                        {days <= 0 ? "Lease expired" : `${days} day${days === 1 ? "" : "s"} remaining`}
                      </div>
                    )}
                  </div>
                );
              })}
              {Array.from({ length: Math.max(0, totalUnits - occupied) }).map((_, i) => (
                <div key={`v${i}`} style={{ padding: "10px 14px", background: "#fff", borderRadius: 8, border: "1px dashed #d4cfc4" }}>
                  <span style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic" }}>Vacant unit</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Open Maintenance Tickets */}
        <Card>
          <h2 style={sectionHead}>Open Maintenance Tickets</h2>
          {openMaint.length === 0 ? (
            <div style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic" }}>No open maintenance tickets.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {openMaint.map((m, i) => (
                <div key={i} style={{ padding: "10px 14px", background: "#F5F1EA", borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#2B2B2B", flex: 1, lineHeight: 1.4 }}>{m.issue}</div>
                    <Badge label={m.status === "in_progress" ? "In Progress" : m.status || "open"} color={statusColor[m.status] || "#dc2626"} />
                  </div>
                  <div style={{ fontSize: 12, color: "#9B9690", marginTop: 5 }}>
                    {m.tenant && <span>{m.tenant} &middot; </span>}
                    {fmtDate(m.date)}
                    {m.priority && <span style={{ color: statusColor[m.priority] || "#9B9690" }}> &middot; {m.priority}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Payment History */}
      <Card style={{ marginBottom: 20 }}>
        <h2 style={sectionHead}>Payment History {currentYear}</h2>
        {yearPayments.length === 0 ? (
          <div style={{ fontSize: 13, color: "#9B9690", fontStyle: "italic" }}>No payments recorded for {currentYear}.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Tenant", "Amount", "Due Date", "Paid Date", "Status"].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {yearPayments.map((p, i) => {
                  const isOverdue = p.status === "overdue";
                  const total = (Number(p.amount) || 0) + (Number(p.late_fee) || 0);
                  return (
                    <tr key={i} style={{ background: isOverdue ? "#FEF2F2" : "transparent" }}>
                      <td style={tdStyle}>{p.tenant}</td>
                      <td style={tdStyle}>
                        ${fmt(total)}
                        {Number(p.late_fee) > 0 && (
                          <span style={{ fontSize: 11, color: "#dc2626", marginLeft: 4 }}>+${fmt(p.late_fee)} fee</span>
                        )}
                      </td>
                      <td style={tdStyle}>{fmtDate(p.due)}</td>
                      <td style={tdStyle}>{p.paid ? fmtDate(p.paid) : <span style={{ color: "#9B9690" }}>&mdash;</span>}</td>
                      <td style={tdStyle}>
                        <Badge label={p.status} color={statusColor[p.status] || "#9B9690"} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Expense Summary */}
      <Card style={{ marginBottom: 20 }}>
        <h2 style={sectionHead}>Expense Summary {currentYear}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: ytdExpenses.length > 0 ? 18 : 0 }}>
          {[
            { label: "Collected YTD", value: `$${fmt(collectedYTD)}`, color: "#163C2F" },
            { label: "Total Expenses", value: `$${fmt(totalExpenses)}`, color: "#dc2626" },
            { label: "Net Profit", value: `${netProfit < 0 ? "-" : ""}$${fmt(Math.abs(netProfit))}`, color: netProfit >= 0 ? "#163C2F" : "#dc2626" },
          ].map(({ label, value, color }) => (
            <div key={label}>
              <div style={{ fontSize: 10, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4, fontFamily: "'Manrope', sans-serif" }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color, fontFamily: "'Manrope', sans-serif", lineHeight: 1 }}>{value}</div>
            </div>
          ))}
        </div>
        {ytdExpenses.length > 0 && (
          <div style={{ borderTop: "1px solid #E8E4DC", paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: "#9B9690", marginBottom: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Logged Expenses</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {ytdExpenses.slice(0, 8).map((e, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                  <span style={{ color: "#2B2B2B" }}>{e.description || e.category}</span>
                  <span style={{ color: "#9B9690", flexShrink: 0, marginLeft: 12 }}>${fmt(e.amount)} &middot; {fmtDate(e.date)}</span>
                </div>
              ))}
              {ytdExpenses.length > 8 && (
                <div style={{ fontSize: 12, color: "#9B9690", fontStyle: "italic" }}>
                  + {ytdExpenses.length - 8} more expense{ytdExpenses.length - 8 !== 1 ? "s" : ""}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Property Notes */}
      <div style={{ marginTop: 20 }}>
        <PropertyNotes property={property} supabase={supabase} session={session} />
      </div>
    </div>
  );
}
